import React, { useState } from 'react';
import { Collapse, Rating as MuiRating, useMediaQuery } from '@mui/material';
import { StarOutlineRounded, StarRounded, ExpandMore, ExpandLess } from '@mui/icons-material';
import { Avatar, Checkbox, Chip, Colors, Text } from 'library';
import { PermissionType, useUserContext } from 'UserContext';
import { ITeacher } from 'models';
import theme from 'theme';
import {
    CardWrapper,
    TopRightAction,
    CardTopRow,
    MobileTopRow,
    MobileContent,
    MobileBottomRow,
    CheckboxContainer,
    AvatarContainer,
    StyledProfileImage,
    InfoContainer,
    NameRow,
    ShowcaseBadge,
    CredentialLine,
    TagsRow,
    RightSection,
    RatingRow,
    BioSection,
    BioDivider,
} from './ShowcaseTeacherRow.styles';

export default function ShowcaseTeacherRow({
    worker,
    selected,
    onChange,
    showcaseHighlight,
    rightAction,
}: {
    worker: ITeacher;
    selected?: boolean;
    onChange?: (worker: ITeacher) => void;
    showcaseHighlight?: boolean;
    rightAction?: React.ReactNode;
}) {
    const { hasPermission } = useUserContext();
    const canPostJobs = hasPermission(PermissionType.PostJob);
    const showCheckbox = canPostJobs && onChange;
    const reviews = worker.credentials.reviews;
    const hasReviews = reviews && reviews.average_score > 0;
    const [expanded, setExpanded] = useState(false);
    const small = useMediaQuery(theme.breakpoints.down('sm'));

    if (small) {
        return (
            <CardWrapper
                showcase={!!showcaseHighlight}
                onClick={() => worker.bio && setExpanded(!expanded)}
                style={{ cursor: worker.bio ? 'pointer' : 'default' }}
            >
                {rightAction && <TopRightAction onClick={(e) => e.stopPropagation()}>{rightAction}</TopRightAction>}
                <MobileTopRow>
                    <AvatarContainer>
                        {worker.pictureUrl ? (
                            <Avatar src={worker.pictureUrl} style={{ height: 44, width: 44 }} />
                        ) : (
                            <StyledProfileImage />
                        )}
                    </AvatarContainer>
                    <NameRow>
                        <Text bold variant="body2">
                            {worker.name}
                        </Text>
                        {showcaseHighlight && (
                            <ShowcaseBadge>
                                <Text variant="caption" bold color={Colors.white}>
                                    SHOWCASE
                                </Text>
                            </ShowcaseBadge>
                        )}
                    </NameRow>
                    {showCheckbox && (
                        <CheckboxContainer onClick={(e) => e.stopPropagation()}>
                            <Checkbox label="" checked={!!selected} onChange={() => onChange!(worker)} />
                        </CheckboxContainer>
                    )}
                </MobileTopRow>
                <MobileContent>
                    {worker.credentials.education.length > 0 && (
                        <CredentialLine>
                            <Text variant="caption" bold>
                                Education:
                            </Text>
                            <Text variant="caption" textStyle={{ marginLeft: 4 }}>
                                {worker.credentials.education.join(', ')}
                            </Text>
                        </CredentialLine>
                    )}
                    {worker.credentials.experience && (
                        <CredentialLine>
                            <Text variant="caption" bold>
                                Experience:
                            </Text>
                            <Text variant="caption" textStyle={{ marginLeft: 4 }}>
                                {worker.credentials.experience}
                            </Text>
                        </CredentialLine>
                    )}
                    <TagsRow>
                        {worker.credentials.highlights?.map((highlight) => (
                            <Chip
                                key={highlight}
                                label={highlight}
                                backgroundColor={Colors.lightTurq}
                                textColor={Colors.turquoise}
                                style={{ height: 24, fontSize: 12 }}
                            />
                        ))}
                    </TagsRow>
                    <MobileBottomRow>
                        <RatingContent reviews={reviews} hasReviews={hasReviews} />
                        {worker.bio && <ExpandIcon expanded={expanded} />}
                    </MobileBottomRow>
                </MobileContent>
                {worker.bio && (
                    <Collapse in={expanded}>
                        <BioSection>
                            <BioDivider />
                            <Text variant="body2" textStyle={{ lineHeight: 1.6 }}>
                                {worker.bio}
                            </Text>
                        </BioSection>
                    </Collapse>
                )}
            </CardWrapper>
        );
    }

    return (
        <CardWrapper showcase={!!showcaseHighlight}>
            {rightAction && <TopRightAction onClick={(e) => e.stopPropagation()}>{rightAction}</TopRightAction>}
            <CardTopRow
                onClick={() => worker.bio && setExpanded(!expanded)}
                style={{ cursor: worker.bio ? 'pointer' : 'default' }}
            >
                {showCheckbox && (
                    <CheckboxContainer onClick={(e) => e.stopPropagation()}>
                        <Checkbox label="" checked={!!selected} onChange={() => onChange!(worker)} />
                    </CheckboxContainer>
                )}
                <AvatarContainer>
                    {worker.pictureUrl ? (
                        <Avatar src={worker.pictureUrl} style={{ height: 56, width: 56 }} />
                    ) : (
                        <StyledProfileImage />
                    )}
                </AvatarContainer>
                <InfoContainer>
                    <NameRow>
                        <Text bold variant="body1">
                            {worker.name}
                        </Text>
                        {showcaseHighlight && (
                            <ShowcaseBadge>
                                <Text variant="caption" bold color={Colors.white}>
                                    SHOWCASE
                                </Text>
                            </ShowcaseBadge>
                        )}
                    </NameRow>
                    {worker.credentials.education.length > 0 && (
                        <CredentialLine>
                            <Text variant="body2" bold>
                                Education:
                            </Text>
                            <Text variant="body2" textStyle={{ marginLeft: 4 }}>
                                {worker.credentials.education.join(', ')}
                            </Text>
                        </CredentialLine>
                    )}
                    {worker.credentials.experience && (
                        <CredentialLine>
                            <Text variant="body2" bold>
                                Experience:
                            </Text>
                            <Text variant="body2" textStyle={{ marginLeft: 4 }}>
                                {worker.credentials.experience}
                            </Text>
                        </CredentialLine>
                    )}
                    <TagsRow>
                        {worker.credentials.highlights?.map((highlight) => (
                            <Chip
                                key={highlight}
                                label={highlight}
                                backgroundColor={Colors.lightTurq}
                                textColor={Colors.turquoise}
                                style={{ height: 24, fontSize: 12 }}
                            />
                        ))}
                    </TagsRow>
                </InfoContainer>
                <RightSection>
                    <RatingContent reviews={reviews} hasReviews={hasReviews} />
                    {worker.bio && <ExpandIcon expanded={expanded} />}
                </RightSection>
            </CardTopRow>
            {worker.bio && (
                <Collapse in={expanded}>
                    <BioSection>
                        <BioDivider />
                        <Text variant="body2" textStyle={{ lineHeight: 1.6 }}>
                            {worker.bio}
                        </Text>
                    </BioSection>
                </Collapse>
            )}
        </CardWrapper>
    );
}

function RatingContent({
    reviews,
    hasReviews,
}: {
    reviews: { average_score: number; total_reviews: number } | null;
    hasReviews: boolean | null;
}) {
    return hasReviews && reviews ? (
        <RatingRow>
            <MuiRating
                name="read-only"
                value={reviews.average_score}
                readOnly
                icon={<StarRounded style={{ height: 16, width: 16, color: '#ffe200' }} />}
                emptyIcon={<StarOutlineRounded style={{ height: 16, width: 16, color: Colors.lightGrey }} />}
                precision={0.1}
                size="small"
            />
            <Text variant="caption" textStyle={{ marginLeft: 4 }}>
                ({reviews.total_reviews})
            </Text>
        </RatingRow>
    ) : (
        <Text variant="caption" textStyle={{ opacity: 0.5 }}>
            No reviews yet
        </Text>
    );
}

function ExpandIcon({ expanded }: { expanded: boolean }) {
    return expanded ? (
        <ExpandLess style={{ fontSize: 22, color: Colors.darkNavy }} />
    ) : (
        <ExpandMore style={{ fontSize: 22, color: Colors.darkNavy }} />
    );
}

